<template>
  <TableList
    v-rect-loading="loading"
    class="onboarding-invitation-list"
    row-key="id"
    hideOnClick
    :title="$route.meta.title"
    :listData="listData"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :disabledActionBtn="disabledActionBtn"
    :actionBtnTooltip="actionBtnTooltip"
    :showCreateBtn="false"
    :pagination="pagination"
    @pagination-change="handlePaginationChange"
  >
    <template #filter>
      <span class="filter-keywords">
        <el-input
          v-model="searchCondition.keywords"
          clearable
          :placeholder="$t(`${i18nPrefix}.label.keywordsSearch`)"
          @input="handleKeywordsChange"
        />
      </span>
      <span class="filter-status">
        <el-select v-model="searchCondition.status" clearable :placeholder="$t(`${i18nPrefix}.placeholder.status`)" @change="handleSearch">
          <el-option
            v-for="item in statusList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          >
          </el-option>
        </el-select>
      </span>
    </template>
    <template #btn>
      <el-button v-rect-loading="exportLoading" class="export-btn" @click="onExportClick">{{ $t(`${i18nPrefix}.label.export`) }}</el-button>
    </template>
    <template #job="{ row }">
      <span v-if="row.job && row.is_candidate_apply_job" class="can-click" @click="onJobClick(row)">
        {{ row.job.name }}
      </span>
      <template v-else>{{ row.job && row.job.name ? row.job.name: '-'}}</template>
    </template>
    <template #candidate="{ row }">
      <span v-if="row.candidate && row.job && row.is_candidate_apply_job" class="can-click" @click="onCandidateClick(row)">
        {{ row.candidate && row.candidate.name }}
      </span>
      <template v-else>{{ row.candidate && row.candidate.name ? row.candidate.name: '-' }}</template>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { formatDate } from '@/components/filters'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    TableList
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.onboarding',
      searchCondition: {
        keywords: null,
        status: null
      },
      listData: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      exportLoading: false
    }
  },
  computed: {
    statusList () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.statusList.invited`),
          value: '1'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.statusList.finished`),
          value: '2'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.statusList.cancelled`),
          value: '3'
        }
      ]
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.invitationDate`),
          dataKey: 'last_reminder_time',
          formatter: (data) => formatDate(data)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.onboardingDate`),
          dataKey: 'onboarding_date',
          formatter: (data) => formatDate(data)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'company',
          formatter: (data) => data.i18nName
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.job`),
          dataKey: 'job',
          slotName: 'job'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.microsite`),
          dataKey: 'microsite_page',
          formatter: (data) => data && data.name ? data.name : '-'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.questionnaire`),
          dataKey: 'questionnaire',
          formatter: (data) => data && data.name ? data.name : '-'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.recruiter`),
          dataKey: 'recruiter_full_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.candidate`),
          dataKey: 'candidate',
          slotName: 'candidate'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          formatter: (data) => {
            if (data === 1) {
              return this.$t(`${this.i18nPrefix}.label.statusList.invited`)
            } else if (data === 2) {
              return this.$t(`${this.i18nPrefix}.label.statusList.finished`)
            } else if (data === 3) {
              return this.$t(`${this.i18nPrefix}.label.statusList.cancelled`)
            }
          }
        }
      ]
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    actionBtn () {
      return {
        notice: {
          show: (rowData) => rowData.status === 1,
          onclick: this.onNoticeClick,
          labelI18nKey: `${this.i18nPrefix}.label.operationBtn.notice`
        },
        cancel: {
          show: (rowData) => rowData.status === 1,
          onclick: this.onCancelClick,
          labelI18nKey: `${this.i18nPrefix}.label.operationBtn.cancel`
        },
        view: {
          show: (rowData) => rowData.status === 2,
          onclick: this.onCandidateClick,
          labelI18nKey: `${this.i18nPrefix}.label.operationBtn.view`
        }
      }
    }
  },
  created () {
    this.fetchListData(true)
  },
  methods: {
    ...mapActions('onboardingManagement', [
      'searchInvitation',
      'createInvitation',
      'putInvitation',
      'createExportOnboardingInvitationTask',
      'getExportOnboardingInvitationTaskStatus'
    ]),
    async fetchListData (reload) {
      this.loading = true
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      try {
        const resp = await this.searchInvitation({
          keywords: this.searchCondition.keywords,
          status: this.searchCondition.status,
          page_num: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        if (resp) {
          _.forEach(resp.invitations, invitation => {
            addI18nProperty(invitation.company, 'i18nName', 'cn_name', 'name')
          })
          this.listData = resp.invitations
          this.pagination.total = resp.total
        }
      } finally {
        this.loading = false
      }
    },
    disabledActionBtn (rowData) {
      return this.hasOnlyActionOnMyCandidatePermission && !rowData.job.is_my_job
    },
    actionBtnTooltip (rowData) {
      return this.disabledActionBtn(rowData) ? this.$t('admin.application.applicantList.message.disabledActionCandidateInJob') : null
    },
    onExportClick () {
      this.exportLoading = true
      this.taskId = new AsyncTask(() => this.createExportOnboardingInvitationTask(
        this.searchCondition
      ), (taskId) => this.getExportOnboardingInvitationTaskStatus({
        task_id: taskId
      }), (taskId) => {
        const exportUrl = `/django_rest/onboarding-management/export-invitation/?task_id=${taskId}&download=1`
        window.location.href = this.$DjangoBaseUrl(exportUrl)
        this.exportLoading = false
      })
    },
    onJobClick (rowData) {
      const job = rowData.job
      if (job) {
        const url = `/v-admin/job/${job.id}/application`
        window.open(url)
      }
    },
    async onNoticeClick (rowData) {
      this.loading = true
      const job = rowData.job
      const candidate = rowData.candidate
      try {
        if (job && candidate) {
          const invitation = {
            is_retry: true,
            job_id: job.id,
            candidate_ids: [candidate.id]
          }
          await this.createInvitation(invitation)
          this.$message.success(this.$t('admin.company.onboarding.label.noticeSuccessMessage'))
        }
      } finally {
        this.loading = false
      }
    },
    async onCancelClick (rowData) {
      this.loading = true
      const id = rowData.id
      try {
        const invitation = {
          id: id
        }
        await this.putInvitation(invitation)
        this.$message.success(this.$t('common.message.optionSuccess'))
      } finally {
        this.loading = false
      }
      this.fetchListData()
    },
    onCandidateClick (rowData) {
      const job = rowData.job
      const candidate = rowData.candidate
      if (job && candidate) {
        const url = `/v-admin/application/profile/onboarding?&candidate_id=${candidate.id}&job_id=${job.id}`
        window.open(url)
      }
    },
    handlePaginationChange () {
      this.fetchListData()
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleSearch, 1000)
      }
      this.keywordsSearchDebounce()
    },
    handleSearch () {
      this.fetchListData()
    }
  }
}
</script>

<style lang="scss" scoped>
.onboarding-invitation-list {
  .export-btn {
    ::v-deep {
      .el-loading-mask {
        .el-loading-spinner {
          margin-top: -14px;

          .spinner {
            height: 26px;
          }
        }
      }
    }
  }

  .can-click {
    cursor: pointer;
    color: #0096cc;

    &:hover {
      color: #33abd6;
    }
  }
}
</style>
